import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import type { InvestorTask } from '../../data/types';
import { useRegistry } from '../../store/RegistryStore';
import { useToast } from '../../ui/feedback';

function VerifyStep({ onDone }: { onDone: () => void }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setProgress((value) => Math.min(100, value + 20)), 300);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (progress >= 100) onDone();
  }, [progress, onDone]);
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Checking your driver licence and the electoral roll…
      </Typography>
      <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
    </Box>
  );
}

export default function TaskActionDialog({
  task,
  onClose,
}: {
  task: InvestorTask | null;
  onClose: () => void;
}) {
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [tfn, setTfn] = useState('');
  const [file, setFile] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [bsb, setBsb] = useState('');
  const [account, setAccount] = useState('');

  const close = () => {
    setTfn('');
    setFile('');
    setVerifying(false);
    setVerified(false);
    setBsb('');
    setAccount('');
    onClose();
  };
  const complete = (message: string, value?: string) => {
    if (!task) return;
    dispatch({ type: 'TASK_COMPLETE', id: task.id, value });
    toast(message);
    close();
  };
  const tfnValid = /^\d{8,9}$/.test(tfn.replace(/\s/g, ''));

  return (
    <Dialog open={Boolean(task)} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{task?.title}</DialogTitle>
      <DialogContent>
        {task && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {task.detail}
          </Typography>
        )}
        {task?.kind === 'QUOTE_TFN' && (
          <>
            <TextField
              label="Tax file number"
              value={tfn}
              onChange={(event) => setTfn(event.target.value)}
              fullWidth
              inputProps={{ inputMode: 'numeric' }}
              helperText="8 or 9 digits. Stored encrypted; we only ever show the last three digits."
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              Quoting a TFN is optional, but without it we must withhold tax from your
              distributions.
            </Alert>
          </>
        )}
        {task?.kind === 'RENEW_WHOLESALE' && (
          <Box
            component="label"
            sx={{
              display: 'block',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: '6px',
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <input
              type="file"
              hidden
              accept=".pdf,.jpg,.png"
              onChange={(event) => setFile(event.target.files?.[0]?.name ?? '')}
            />
            <CloudUploadOutlinedIcon color="action" />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {file || "Upload your accountant's certificate (PDF)"}
            </Typography>
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={(event) => {
                event.preventDefault();
                setFile('accountant-certificate-2026.pdf');
              }}
            >
              Use a sample file
            </Button>
          </Box>
        )}
        {task?.kind === 'CONFIRM_BANK' && (
          <>
            <TextField
              label="BSB"
              value={bsb}
              onChange={(event) => setBsb(event.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Account number"
              value={account}
              onChange={(event) => setAccount(event.target.value)}
              fullWidth
              inputProps={{ inputMode: 'numeric' }}
              helperText="Must be in the investing entity's name. We'll send a code to your mobile to confirm."
            />
          </>
        )}
        {task?.kind === 'REFRESH_KYC' &&
          (verified ? (
            <Alert icon={<VerifiedUserOutlinedIcon />} severity="success">
              Identity verified. Thank you.
            </Alert>
          ) : verifying ? (
            <VerifyStep onDone={() => setVerified(true)} />
          ) : (
            <Typography variant="body2">
              We&apos;ll check your details electronically with GreenID. It takes about a minute and
              doesn&apos;t affect your credit file.
            </Typography>
          ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close}>Cancel</Button>
        {task?.kind === 'QUOTE_TFN' && (
          <Button
            variant="contained"
            disabled={!tfnValid}
            onClick={() =>
              complete(
                'TFN saved. Future distributions will be paid without withholding.',
                tfn.replace(/\s/g, ''),
              )
            }
          >
            Save TFN
          </Button>
        )}
        {task?.kind === 'RENEW_WHOLESALE' && (
          <Button
            variant="contained"
            disabled={!file}
            onClick={() => complete('Certificate uploaded and accepted')}
          >
            Submit
          </Button>
        )}
        {task?.kind === 'CONFIRM_BANK' && (
          <Button
            variant="contained"
            disabled={bsb.replace(/\D/g, '').length !== 6 || account.replace(/\D/g, '').length < 6}
            onClick={() =>
              complete(
                'Bank details confirmed. Your payment will be reissued in the next payment run.',
                account,
              )
            }
          >
            Confirm details
          </Button>
        )}
        {task?.kind === 'REFRESH_KYC' && !verified && (
          <Button variant="contained" disabled={verifying} onClick={() => setVerifying(true)}>
            Start verification
          </Button>
        )}
        {task?.kind === 'REFRESH_KYC' && verified && (
          <Button variant="contained" onClick={() => complete('Identity re-verified')}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
